import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Bars3Icon, Cog6ToothIcon, PencilSquareIcon, PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useMutation } from "@tanstack/react-query";
import { type FC, useEffect, useState } from "react";

import { PlaceholderEditor, Small } from "@fronterahealth/frontera-ui-components";

import {
  type Curriculum,
  type OrganizationSummary,
  type TreatmentPlanTemplateData,
  type TreatmentPlanTemplateDomain,
  type TreatmentPlanTemplateField,
  updateTemplate,
} from "@api/rest/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { TemplateSaveFooter } from "@pages/Templates/TemplateSaveFooter";
import { OptionsEditor } from "@pages/Templates/TemplateStructure";
import { TreatmentPlanSettingsDialog } from "@pages/Templates/TreatmentPlanSettingsDialog";
import {
  FieldType,
  GoalFieldIdentifier,
  TPFieldType,
  type TPFieldTypeValue,
  TargetFieldIdentifier,
} from "@pages/Templates/constants";
import {
  EDITOR_CONTENT_INSET_CLASS,
  PlaceholderEditorVariant,
  PlaceholderValues,
  convertDisplayPlaceholdersToKeys,
  convertKeyPlaceholdersToDisplay,
  htmlToMarkdownIfNeeded,
} from "@pages/Templates/placeholders";

// --- Programming Tab ---

const TP_FIELD_TYPE_LABELS: Record<string, string> = {
  [FieldType.SIMPLE_TEXT]: "Simple Text",
  [FieldType.WYSIWYG]: "Formatted Text",
  [FieldType.SELECT]: "Dropdown",
  [FieldType.CHECKBOX]: "Checkboxes",
  [FieldType.RADIO]: "Radio Button",
  [FieldType.DATE]: "Date Picker",
  [FieldType.IMAGE]: "Image",
};

export interface LocalTPField {
  localId: string;
  id?: number;
  title: string;
  field_type: string;
  rank_order: number;
  is_required: boolean;
  is_enabled: boolean;
  field_identifier: string;
  type: TPFieldTypeValue;
  report_goal_field: boolean;
  report_target_field: boolean;
  content: string | null;
}

interface LocalTPDomain {
  localId: string;
  id?: number;
  name: string;
  description: string | null;
  rank_order: number;
  fields: LocalTPField[];
}

const TP_FIELDS_WITH_OPTIONS = new Set<string>([FieldType.SELECT, FieldType.CHECKBOX, FieldType.RADIO]);

// Native goal/target columns backed by a real DateField (report_template_service.py's
// NATIVE_DATE_COLUMNS) — the only value that can ever be correct is Date Picker. Letting
// an admin pick anything else looked harmless in the UI but broke the AI-fillability check
// and export formatting once saved (PROC-146). The backend now rejects the edit outright;
// locking the control here is what keeps the admin from hitting that error at all.
const NATIVE_DATE_FIELD_IDENTIFIERS = new Set(["baseline_date", "completion_date", "projected_introduction_date"]);

// Every native column not in NATIVE_DATE_FIELD_IDENTIFIERS is a plain
// TextField — it can hold Simple Text, Formatted Text, Dropdown, Radio
// Button, or Checkboxes (all ultimately a string; orgs legitimately
// configure e.g. generalization_criteria as a dropdown). Date Picker is
// the one choice that doesn't fit (report_template_service.py's
// reject_native_field_retyped_to_date).

function parseTPOptions(content: string | null): string[] {
  if (!content) return [];
  try {
    const parsed = JSON.parse(content) as { options?: string[] };
    return Array.isArray(parsed.options) ? parsed.options : [];
  } catch {
    return [];
  }
}

function buildTPContent(options: string[], fieldType: string): string {
  return JSON.stringify({ options, answer: fieldType === FieldType.CHECKBOX ? [] : null });
}

function toLocalTPField(f: TreatmentPlanTemplateField): LocalTPField {
  return {
    localId: String(f.id),
    id: f.id,
    title: f.title ?? "",
    field_type: f.field_type,
    rank_order: f.rank_order,
    is_required: f.is_required,
    is_enabled: f.is_enabled,
    field_identifier: f.field_identifier,
    type: f.type as TPFieldTypeValue,
    report_goal_field: f.report_goal_field,
    report_target_field: f.report_target_field,
    content: f.content ?? null,
  };
}

function toLocalTPDomain(d: TreatmentPlanTemplateDomain): LocalTPDomain {
  return {
    localId: String(d.id),
    id: d.id,
    name: d.name,
    // Stored key-form placeholders ({clientFirstName}) -> chip display form for
    // the editor, and normalize any legacy HTML to markdown. Mirrors field content.
    description: htmlToMarkdownIfNeeded(convertKeyPlaceholdersToDisplay(d.description ?? "")) || null,
    rank_order: d.rank_order,
    fields: [...d.template_fields].sort((a, b) => a.rank_order - b.rank_order).map(toLocalTPField),
  };
}

export const ProgrammingTab: FC<{
  templateId: number;
  treatmentPlanTemplate: TreatmentPlanTemplateData;
  currentCurriculum: Curriculum | null;
  organizations: readonly OrganizationSummary[];
  onDirtyChange?: (dirty: boolean) => void;
  onSaved: () => void;
  isReadOnly?: boolean;
}> = ({
  templateId,
  treatmentPlanTemplate: tpt,
  currentCurriculum,
  organizations,
  onDirtyChange,
  onSaved,
  isReadOnly = false,
}) => {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [domains, setDomains] = useState<LocalTPDomain[]>(() =>
    [...tpt.domains].sort((a, b) => a.rank_order - b.rank_order).map(toLocalTPDomain),
  );
  const [defaultFields, setDefaultFields] = useState<LocalTPField[]>(() =>
    [...tpt.default_fields].sort((a, b) => a.rank_order - b.rank_order).map(toLocalTPField),
  );
  const [changed, setChanged] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(() => new Date());

  useEffect(() => {
    onDirtyChange?.(changed);
  }, [changed, onDirtyChange]);

  // Re-sync from server data on background refetches (e.g. after a Rio
  // edit invalidates the template query), but not while the user has
  // unsaved edits — same pattern as the sections tab.
  useEffect(() => {
    if (!changed) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDomains([...tpt.domains].sort((a, b) => a.rank_order - b.rank_order).map(toLocalTPDomain));
      setDefaultFields([...tpt.default_fields].sort((a, b) => a.rank_order - b.rank_order).map(toLocalTPField));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tpt]);

  const discardChanges = () => {
    setDomains([...tpt.domains].sort((a, b) => a.rank_order - b.rank_order).map(toLocalTPDomain));
    setDefaultFields([...tpt.default_fields].sort((a, b) => a.rank_order - b.rank_order).map(toLocalTPField));
    setChanged(false);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function toSlug(title: string): string {
    return (
      title
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, "")
        .replace(/_+/g, "_")
        .replace(/^_|_$/g, "") || "field"
    );
  }

  function uniqueSlug(base: string, taken: Set<string>): string {
    if (!taken.has(base)) return base;
    let i = 2;
    while (taken.has(`${base}_${i}`)) i++;
    return `${base}_${i}`;
  }

  /** Fields saved without an identifier are unaddressable downstream (QA
   * flags, org AI settings key off field_identifier) — auto-slug from the
   * final title at save time, unique across the whole template. */
  function withGeneratedIdentifier<T extends { field_identifier: string; title: string }>(
    field: T,
    taken: Set<string>,
  ): T {
    if (field.field_identifier) return field;
    const generated = uniqueSlug(toSlug(field.title), taken);
    taken.add(generated);
    return { ...field, field_identifier: generated };
  }

  const saveMutation = useMutation({
    mutationFn: () => {
      const taken = new Set(
        [...domains.flatMap((d) => d.fields), ...defaultFields].map((f) => f.field_identifier).filter(Boolean),
      );
      // Generate identifiers ONCE and write them back into state before
      // sending — otherwise the UI keeps showing a blank key and a later
      // title edit would regenerate a different identifier, overwriting
      // the persisted one (QA flags / org AI settings key off it).
      const nextDomains = domains.map((d) => ({
        ...d,
        fields: d.fields.map((f) => withGeneratedIdentifier(f, taken)),
      }));
      const nextDefaults = defaultFields.map((f) => withGeneratedIdentifier(f, taken));
      setDomains(nextDomains);
      setDefaultFields(nextDefaults);
      return updateTemplate({
        template_id: templateId,
        treatment_plan_domains: nextDomains.map((d, di) => ({
          ...(d.id ? { id: d.id } : {}),
          name: d.name,
          // Chip display form -> stored key form ({clientFirstName}) so FDM's
          // placeholder resolver substitutes it at report creation. Mirrors field content.
          description: convertDisplayPlaceholdersToKeys(d.description ?? "") || null,
          rank_order: di + 1,
          fields: d.fields.map((f, fi) => ({
            ...(f.id ? { id: f.id } : {}),
            title: f.title,
            field_type: f.field_type,
            rank_order: fi + 1,
            is_required: f.is_required,
            is_enabled: f.is_enabled,
            field_identifier: f.field_identifier,
            type: f.type,
            content: f.content,
          })),
        })),
        treatment_plan_default_fields: nextDefaults.map((f, fi) => ({
          ...(f.id ? { id: f.id } : {}),
          title: f.title,
          field_type: f.field_type,
          rank_order: fi + 1,
          is_required: f.is_required,
          is_enabled: f.is_enabled,
          field_identifier: f.field_identifier,
          type: f.type,
          content: f.content,
        })),
      });
    },
    onSuccess: (result) => {
      if (result.status !== 200) {
        // Surface the backend's guard message ({detail} on 400) — e.g.
        // "A domain containing Goal Name ... cannot be removed" is
        // actionable, a generic failure toast is a dead end.
        notifyError(result.data.detail || "Failed to save programming");
        return;
      }
      notifySuccess("Programming saved");
      setChanged(false);
      setLastSavedAt(new Date());
      onSaved();
    },
    onError: () => notifyError("Failed to save programming"),
  });

  // --- Domain handlers ---

  const addDomain = () => {
    setDomains((prev) => [
      ...prev,
      {
        localId: `temp-${crypto.randomUUID()}`,
        name: "New Domain",
        description: null,
        rank_order: prev.length + 1,
        fields: [],
      },
    ]);
    setChanged(true);
  };

  const removeDomain = (localId: string) => {
    setDomains((prev) => prev.filter((d) => d.localId !== localId));
    setChanged(true);
  };

  const updateDomain = (localId: string, patch: Partial<LocalTPDomain>) => {
    setDomains((prev) => prev.map((d) => (d.localId === localId ? { ...d, ...patch } : d)));
    setChanged(true);
  };

  const handleDomainDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDomains((prev) => {
      const oldIndex = prev.findIndex((d) => d.localId === active.id);
      const newIndex = prev.findIndex((d) => d.localId === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
    setChanged(true);
  };

  // --- Domain field handlers ---

  const addFieldToDomain = (domainLocalId: string, type: TPFieldTypeValue) => {
    const newField: LocalTPField = {
      localId: `temp-${crypto.randomUUID()}`,
      title: `New ${type === TPFieldType.GOAL ? "Goal" : "Target"} Field`,
      field_type: FieldType.SIMPLE_TEXT,
      rank_order: 0,
      is_required: false,
      is_enabled: true,
      field_identifier: "",
      type,
      report_goal_field: false,
      report_target_field: false,
      content: null,
    };
    setDomains((prev) =>
      prev.map((d) => (d.localId === domainLocalId ? { ...d, fields: [...d.fields, newField] } : d)),
    );
    setChanged(true);
  };

  const updateFieldInDomain = (domainLocalId: string, fieldLocalId: string, patch: Partial<LocalTPField>) => {
    setDomains((prev) =>
      prev.map((d) =>
        d.localId === domainLocalId
          ? { ...d, fields: d.fields.map((f) => (f.localId === fieldLocalId ? { ...f, ...patch } : f)) }
          : d,
      ),
    );
    setChanged(true);
  };

  const removeFieldFromDomain = (domainLocalId: string, fieldLocalId: string) => {
    setDomains((prev) =>
      prev.map((d) =>
        d.localId === domainLocalId ? { ...d, fields: d.fields.filter((f) => f.localId !== fieldLocalId) } : d,
      ),
    );
    setChanged(true);
  };

  const reorderDomainFields = (
    domainLocalId: string,
    type: TPFieldTypeValue,
    activeLocalId: string,
    overLocalId: string,
  ) => {
    setDomains((prev) =>
      prev.map((d) => {
        if (d.localId !== domainLocalId) return d;
        const typed = d.fields.filter((f) => f.type === type);
        const other = d.fields.filter((f) => f.type !== type);
        const oldIndex = typed.findIndex((f) => f.localId === activeLocalId);
        const newIndex = typed.findIndex((f) => f.localId === overLocalId);
        if (oldIndex === -1 || newIndex === -1) return d;
        const moved = arrayMove(typed, oldIndex, newIndex);
        return { ...d, fields: [...other, ...moved] };
      }),
    );
    setChanged(true);
  };

  // --- Default field handlers ---

  const addDefaultField = (type: TPFieldTypeValue) => {
    setDefaultFields((prev) => [
      ...prev,
      {
        localId: `temp-${crypto.randomUUID()}`,
        title: `New Default ${type === TPFieldType.GOAL ? "Goal" : "Target"} Field`,
        field_type: FieldType.SIMPLE_TEXT,
        rank_order: prev.length + 1,
        is_required: false,
        is_enabled: true,
        field_identifier: "",
        type,
        report_goal_field: false,
        report_target_field: false,
        content: null,
      },
    ]);
    setChanged(true);
  };

  const updateDefaultField = (fieldLocalId: string, patch: Partial<LocalTPField>) => {
    setDefaultFields((prev) => prev.map((f) => (f.localId === fieldLocalId ? { ...f, ...patch } : f)));
    setChanged(true);
  };

  const removeDefaultField = (fieldLocalId: string) => {
    setDefaultFields((prev) => prev.filter((f) => f.localId !== fieldLocalId));
    setChanged(true);
  };

  const reorderDefaultFields = (type: TPFieldTypeValue, activeLocalId: string, overLocalId: string) => {
    setDefaultFields((prev) => {
      const typed = prev.filter((f) => f.type === type);
      const other = prev.filter((f) => f.type !== type);
      const oldIndex = typed.findIndex((f) => f.localId === activeLocalId);
      const newIndex = typed.findIndex((f) => f.localId === overLocalId);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return [...other, ...arrayMove(typed, oldIndex, newIndex)];
    });
    setChanged(true);
  };

  const defaultGoalFields = defaultFields.filter((f) => f.type === TPFieldType.GOAL);
  const defaultTargetFields = defaultFields.filter((f) => f.type === TPFieldType.TARGET);

  return (
    <div>
      {/* Info box */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4 flex gap-2 items-start text-xs">
        <span className="text-blue-600 shrink-0 mt-0.5">ℹ</span>
        <div>
          <div className="font-semibold text-blue-900">Configure Treatment Plan</div>
          <div className="text-blue-800">
            Create programming domains and customize their fields. Default fields apply to all domains. Add custom
            fields per domain, reorder by dragging, and toggle required status as needed.
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setSettingsOpen(true)}
          className="flex items-center gap-1.5 rounded-md border border-border-medium px-3 py-1.5 text-sm text-text-secondary hover:bg-bg-default"
        >
          <Cog6ToothIcon className="h-4 w-4" />
          Treatment Plan Settings
        </button>
      </div>

      {/* Default Fields */}
      <SectionLabel>Default</SectionLabel>
      <div className="mb-4">
        <Small displayType="loud" colorType="secondary" className="mb-2 block">
          Goal
        </Small>
        <SortableFieldList
          fields={defaultGoalFields}
          sensors={sensors}
          onReorder={(a, o) => reorderDefaultFields(TPFieldType.GOAL, a, o)}
          onUpdateField={updateDefaultField}
          onRemoveField={removeDefaultField}
          addLabel="Add Default Goal Field"
          onAdd={() => addDefaultField(TPFieldType.GOAL)}
          isReadOnly={isReadOnly}
        />
      </div>
      <div className="mb-6">
        <Small displayType="loud" colorType="secondary" className="mb-2 block">
          Target
        </Small>
        <SortableFieldList
          fields={defaultTargetFields}
          sensors={sensors}
          onReorder={(a, o) => reorderDefaultFields(TPFieldType.TARGET, a, o)}
          onUpdateField={updateDefaultField}
          onRemoveField={removeDefaultField}
          addLabel="Add Default Target Field"
          onAdd={() => addDefaultField(TPFieldType.TARGET)}
          isReadOnly={isReadOnly}
        />
      </div>

      {/* Domains */}
      <SectionLabel>Domains</SectionLabel>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDomainDragEnd}>
        <SortableContext items={domains.map((d) => d.localId)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3 mb-4">
            {domains.length === 0 ? (
              <div className="text-center rounded-lg bg-bg-surface border border-border-subtle p-4">
                <Small colorType="secondary">
                  <em>No domains configured. Add your first domain below.</em>
                </Small>
              </div>
            ) : (
              domains.map((domain) => (
                <SortableDomainCard
                  key={domain.localId}
                  domain={domain}
                  sensors={sensors}
                  onUpdate={(patch) => updateDomain(domain.localId, patch)}
                  onRemove={() => removeDomain(domain.localId)}
                  onAddField={(type) => addFieldToDomain(domain.localId, type)}
                  onUpdateField={(fLocalId, patch) => updateFieldInDomain(domain.localId, fLocalId, patch)}
                  onRemoveField={(fLocalId) => removeFieldFromDomain(domain.localId, fLocalId)}
                  onReorderFields={(type, a, o) => reorderDomainFields(domain.localId, type, a, o)}
                  isReadOnly={isReadOnly}
                />
              ))
            )}
          </div>
        </SortableContext>
      </DndContext>

      {!isReadOnly && (
        <button
          onClick={addDomain}
          className="flex items-center gap-1.5 rounded-lg border border-dashed border-border-medium px-4 py-2 text-sm text-text-secondary hover:bg-bg-default w-full justify-center"
        >
          <PlusIcon className="h-4 w-4" />
          Add Domain
        </button>
      )}

      <TreatmentPlanSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        tpt={tpt}
        currentCurriculum={currentCurriculum}
        templateId={templateId}
        organizations={organizations}
        onSaved={onSaved}
      />

      {!isReadOnly && (
        <TemplateSaveFooter
          isDirty={changed}
          isSaving={saveMutation.isPending}
          onSave={() => saveMutation.mutate()}
          onDiscard={discardChanges}
          lastSavedAt={lastSavedAt}
        />
      )}
    </div>
  );
};

// --- Section label ---

const SectionLabel: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mb-2">
    <span className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">{children}</span>
    <div className="flex-1 h-px bg-border-subtle mt-1 mb-3" />
  </div>
);

// --- Sortable field list (wraps DnD context around field rows) ---

type Sensors = ReturnType<typeof useSensors>;

const SortableFieldList: FC<{
  fields: LocalTPField[];
  sensors: Sensors;
  onReorder: (activeLocalId: string, overLocalId: string) => void;
  onUpdateField: (localId: string, patch: Partial<LocalTPField>) => void;
  onRemoveField: (localId: string) => void;
  addLabel: string;
  onAdd: () => void;
  isReadOnly?: boolean;
}> = ({ fields, sensors, onReorder, onUpdateField, onRemoveField, addLabel, onAdd, isReadOnly = false }) => {
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onReorder(String(active.id), String(over.id));
  };

  return (
    <div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={fields.map((f) => f.localId)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {fields.length === 0 ? (
              <p className="text-sm text-text-tertiary italic">No fields.</p>
            ) : (
              fields.map((f) => (
                <SortableFieldRow
                  key={f.localId}
                  field={f}
                  onUpdate={(patch) => onUpdateField(f.localId, patch)}
                  onRemove={() => onRemoveField(f.localId)}
                  isReadOnly={isReadOnly}
                />
              ))
            )}
          </div>
        </SortableContext>
      </DndContext>
      {!isReadOnly && (
        <button onClick={onAdd} className="mt-2 flex items-center gap-1 text-xs text-rio-600 hover:text-rio-800">
          <PlusIcon className="h-3.5 w-3.5" />
          {addLabel}
        </button>
      )}
    </div>
  );
};

// --- Sortable domain card ---

const SortableDomainCard: FC<{
  domain: LocalTPDomain;
  sensors: Sensors;
  onUpdate: (patch: Partial<LocalTPDomain>) => void;
  onRemove: () => void;
  onAddField: (type: TPFieldTypeValue) => void;
  onUpdateField: (localId: string, patch: Partial<LocalTPField>) => void;
  onRemoveField: (localId: string) => void;
  onReorderFields: (type: TPFieldTypeValue, activeLocalId: string, overLocalId: string) => void;
  isReadOnly?: boolean;
}> = ({
  domain,
  sensors,
  onUpdate,
  onRemove,
  onAddField,
  onUpdateField,
  onRemoveField,
  onReorderFields,
  isReadOnly = false,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: domain.localId,
    disabled: isReadOnly,
  });
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState(domain.name);

  // Background re-syncs (e.g. a Rio rename) replace `domain`; keep the
  // name editor's seed current so opening it doesn't resurrect a stale
  // title and write it back over the rename on blur.
  useEffect(() => {
    if (!editingName) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setNameVal(domain.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain.name]);

  const goalFields = domain.fields.filter((f) => f.type === TPFieldType.GOAL);
  const targetFields = domain.fields.filter((f) => f.type === TPFieldType.TARGET);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: isDragging ? ("relative" as const) : undefined,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="rounded-2xl border border-border-strong bg-white overflow-hidden">
      {/* Domain header */}
      <div className="flex items-center justify-between px-4 py-3 bg-bg-surface border-b border-limestone-100">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {!isReadOnly && (
            <button
              {...attributes}
              {...listeners}
              className="h-6 w-6 p-1 mr-1 rounded hover:bg-limestone-50 text-text-tertiary shrink-0 cursor-grab active:cursor-grabbing touch-none"
              aria-label="Drag to reorder"
            >
              <Bars3Icon className="h-4 w-4" />
            </button>
          )}
          {editingName && !isReadOnly ? (
            <input
              autoFocus
              value={nameVal}
              onChange={(e) => setNameVal(e.target.value)}
              onBlur={() => {
                onUpdate({ name: nameVal });
                setEditingName(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  onUpdate({ name: nameVal });
                  setEditingName(false);
                }
                if (e.key === "Escape") {
                  setNameVal(domain.name);
                  setEditingName(false);
                }
              }}
              className="flex-1 rounded border border-border-medium px-2 py-0.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-limestone-500"
            />
          ) : (
            <button
              onClick={() => !isReadOnly && setEditingName(true)}
              className={`text-sm font-semibold text-text-primary text-left truncate ${!isReadOnly ? "hover:text-rio-600" : "cursor-default"}`}
            >
              {domain.name}
            </button>
          )}
        </div>
        {!isReadOnly && (
          <button
            onClick={onRemove}
            className="p-1.5 rounded text-text-tertiary hover:text-text-error-primary hover:bg-red-50 shrink-0"
            aria-label="Remove domain"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Domain description */}
      <div className="px-4 py-3 border-b border-limestone-100">
        <Small displayType="loud" colorType="secondary" className="mb-2 block">
          Description
        </Small>
        {isReadOnly && !domain.description ? (
          <p className="text-sm italic text-text-tertiary">No description set</p>
        ) : (
          <PlaceholderEditor
            key={`domain-desc-${domain.localId}`}
            className={EDITOR_CONTENT_INSET_CLASS}
            variant={PlaceholderEditorVariant.WYSIWYG}
            existingContent={domain.description ?? ""}
            hintText={isReadOnly ? undefined : "Type / for dynamic content"}
            insertValues={PlaceholderValues}
            onChange={(v) => onUpdate({ description: v })}
            placeholder="Optional domain description (renders above the domain's goals in the export)"
            readonly={isReadOnly}
          />
        )}
      </div>

      {/* Domain fields */}
      <div className="px-4 py-3 space-y-4">
        <div>
          <Small displayType="loud" colorType="secondary" className="mb-2 block">
            Goal Fields
          </Small>
          <SortableFieldList
            fields={goalFields}
            sensors={sensors}
            onReorder={(a, o) => onReorderFields(TPFieldType.GOAL, a, o)}
            onUpdateField={onUpdateField}
            onRemoveField={onRemoveField}
            addLabel="Add Goal Field"
            onAdd={() => onAddField(TPFieldType.GOAL)}
            isReadOnly={isReadOnly}
          />
        </div>
        <div>
          <Small displayType="loud" colorType="secondary" className="mb-2 block">
            Target Fields
          </Small>
          <SortableFieldList
            fields={targetFields}
            sensors={sensors}
            onReorder={(a, o) => onReorderFields(TPFieldType.TARGET, a, o)}
            onUpdateField={onUpdateField}
            onRemoveField={onRemoveField}
            addLabel="Add Target Field"
            onAdd={() => onAddField(TPFieldType.TARGET)}
            isReadOnly={isReadOnly}
          />
        </div>
      </div>
    </div>
  );
};

// --- Sortable field row ---

export interface SortableFieldRowProps {
  field: LocalTPField;
  onUpdate: (patch: Partial<LocalTPField>) => void;
  onRemove: () => void;
  isReadOnly?: boolean;
}

export const SortableFieldRow: FC<SortableFieldRowProps> = ({ field, onUpdate, onRemove, isReadOnly = false }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.localId,
    disabled: isReadOnly,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: isDragging ? ("relative" as const) : undefined,
    zIndex: isDragging ? 10 : undefined,
  };

  const isNativeField = field.report_goal_field || field.report_target_field;
  // goal_name / target_name back NOT NULL columns (ReportGoal.goal_name,
  // ReportTarget.target_name) — a goal/target can't exist without a name,
  // so these two rows can never be turned off or deleted. Keyed on
  // nativeness too: a custom field that happens to carry the same
  // identifier string is not the built-in column and stays fully editable.
  const isLockedNameField =
    (field.report_goal_field && field.field_identifier === GoalFieldIdentifier.GOAL_NAME) ||
    (field.report_target_field && field.field_identifier === TargetFieldIdentifier.TARGET_NAME);
  const lockedNameNoun = field.report_target_field ? "target" : "goal";
  // A native row's field_identifier is its join key to the backing
  // ReportGoal/ReportTarget column — immutable (backend-enforced). Custom
  // fields' keys stay editable.
  const canEditIdentifier = !isNativeField && !isReadOnly;
  const isLockedDateField = isNativeField && NATIVE_DATE_FIELD_IDENTIFIERS.has(field.field_identifier);
  // Every other native column is a plain TextField underneath — Simple
  // Text, Formatted Text, Dropdown, Radio Button, and Checkboxes are
  // all just a string in the end, so orgs can freely pick any of them
  // (generalization_criteria as a Dropdown is a real, supported
  // pattern). Date Picker is the one choice that doesn't fit — excluded
  // for every native text column.
  const excludesDateOption = isNativeField && !isLockedDateField;
  const fieldTypeOptions = excludesDateOption
    ? Object.keys(TP_FIELD_TYPE_LABELS).filter((val) => val !== FieldType.DATE || val === field.field_type)
    : Object.keys(TP_FIELD_TYPE_LABELS);

  const hasOptions = TP_FIELDS_WITH_OPTIONS.has(field.field_type);
  const hasDefaultContent = field.field_type === FieldType.SIMPLE_TEXT || field.field_type === FieldType.WYSIWYG;

  const [isEditingKey, setIsEditingKey] = useState(false);

  const handleFieldTypeChange = (newType: string) => {
    const switchingToOptions = TP_FIELDS_WITH_OPTIONS.has(newType);
    const hadOptions = TP_FIELDS_WITH_OPTIONS.has(field.field_type);
    let content = field.content;
    if (switchingToOptions) {
      const existing = hadOptions ? parseTPOptions(field.content) : [];
      const opts = existing.length > 0 ? existing : ["Option 1", "Option 2"];
      content = buildTPContent(opts, newType);
    } else if (hadOptions) {
      // Switching from an options type to one without options (text/date/image):
      // the old content is options JSON, so clear it. Otherwise the raw
      // `{"options":...}` string would surface in the Default Content box (text
      // types) or persist a stale encoding on the field.
      content = "";
    }
    onUpdate({ field_type: newType, content });
  };

  return (
    <div ref={setNodeRef} style={style} className="rounded-lg border border-border-subtle bg-bg-default">
      <div className="flex items-center gap-2 px-3 py-2">
        {!isReadOnly && (
          <button
            {...attributes}
            {...listeners}
            className="h-6 w-6 p-1 mr-1 rounded shrink-0 touch-none hover:bg-limestone-50 text-text-tertiary cursor-grab active:cursor-grabbing"
            aria-label="Drag to reorder"
            type="button"
          >
            <Bars3Icon className="h-4 w-4" />
          </button>
        )}
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <input
            value={field.title}
            onChange={(e) => onUpdate({ title: e.target.value })}
            placeholder="Field title"
            disabled={isReadOnly}
            className="flex-1 min-w-0 text-sm text-text-primary bg-transparent border-none outline-none placeholder:text-text-tertiary disabled:opacity-60"
          />
          <div className="h-3.5 w-px bg-border-subtle shrink-0" />
          {canEditIdentifier && isEditingKey ? (
            <input
              type="text"
              autoFocus
              value={field.field_identifier}
              onChange={(e) => onUpdate({ field_identifier: e.target.value })}
              onBlur={() => setIsEditingKey(false)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === "Escape") {
                  (e.target as HTMLInputElement).blur();
                }
              }}
              placeholder="field_identifier"
              className="shrink-0 text-xs rounded border border-border-medium bg-bg-surface px-2 py-0.5 text-text-secondary focus:outline-none"
            />
          ) : (
            <div className="flex items-center gap-1 shrink-0 group/key">
              <span className="font-mono text-xs text-text-tertiary">{field.field_identifier || "—"}</span>
              {canEditIdentifier && (
                <button
                  type="button"
                  onClick={() => setIsEditingKey(true)}
                  className="opacity-0 group-hover/key:opacity-100 transition-opacity duration-150 p-0.5 rounded hover:bg-limestone-50 text-text-tertiary"
                  aria-label="Edit field key"
                >
                  <PencilSquareIcon className="h-3 w-3" />
                </button>
              )}
            </div>
          )}
        </div>
        {isLockedDateField ? (
          // Not a disabled control — a plain fact about the field. This
          // column always stores a real date; showing a select (even
          // disabled) implied a choice existed, which is what let admins
          // try to change it in the first place before hitting the save
          // error.
          <span
            title="This field always stores a real date, so its type isn't a choice."
            className="text-xs rounded border border-border-subtle bg-bg-default px-2 py-1 text-text-tertiary"
          >
            {TP_FIELD_TYPE_LABELS[field.field_type] ?? field.field_type}
          </span>
        ) : (
          <select
            value={field.field_type}
            onChange={(e) => handleFieldTypeChange(e.target.value)}
            disabled={isReadOnly}
            className="text-xs rounded border border-border-medium bg-bg-surface pl-2 pr-6 py-1 text-text-secondary focus:outline-none disabled:opacity-60"
          >
            {fieldTypeOptions.map((val) => (
              <option key={val} value={val}>
                {TP_FIELD_TYPE_LABELS[val]}
              </option>
            ))}
          </select>
        )}
        <label
          title={
            isLockedNameField ? `Every ${lockedNameNoun} requires a name — this field can't be turned off.` : undefined
          }
          className="flex items-center gap-1 text-xs text-text-secondary cursor-pointer"
        >
          <input
            type="checkbox"
            checked={field.is_enabled}
            onChange={(e) => onUpdate({ is_enabled: e.target.checked })}
            disabled={isReadOnly || isLockedNameField}
            className="h-3.5 w-3.5 rounded border-border-medium text-rio-600 disabled:opacity-60"
          />
          On
        </label>
        <label className="flex items-center gap-1 text-xs text-text-secondary cursor-pointer">
          <input
            type="checkbox"
            checked={field.is_required}
            onChange={(e) => onUpdate({ is_required: e.target.checked })}
            disabled={isReadOnly}
            className="h-3.5 w-3.5 rounded border-border-medium text-rio-600 disabled:opacity-60"
          />
          Required
        </label>
        {!isReadOnly &&
          (isLockedNameField ? (
            <span
              title={`Every ${lockedNameNoun} requires a name — this field can't be removed.`}
              className="p-1 rounded text-text-tertiary opacity-40 cursor-not-allowed shrink-0"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </span>
          ) : (
            <button
              onClick={onRemove}
              className="p-1 rounded text-text-tertiary hover:text-text-error-primary hover:bg-red-50 shrink-0"
              aria-label="Remove field"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          ))}
      </div>

      {hasOptions && (
        <div className={`pr-3 pb-3 pt-1 border-t border-border-subtle ${isReadOnly ? "pl-3" : "pl-8"}`}>
          <OptionsEditor
            options={parseTPOptions(field.content)}
            onChange={(opts) => onUpdate({ content: buildTPContent(opts, field.field_type) })}
            readonly={isReadOnly}
          />
        </div>
      )}

      {hasDefaultContent && (
        <div
          className={`pr-3 pb-3 pt-1 border-t border-border-subtle ${
            // Editable: indent to clear the drag handle so the content lines up
            // with the field title column (handle w-6 + mr-1 + row gap-2 = pl-12).
            // Read-only: no handle, so just a small inset (pl-8).
            isReadOnly ? "pl-8" : "pl-12"
          }`}
        >
          <Small colorType="primary" className="mb-1 block">
            Default Content
          </Small>
          {isReadOnly ? (
            field.content ? (
              // Read-only display: a div with whitespace-pre-wrap (not an
              // <input>, which strips newlines) so multi-line starter text keeps
              // its line breaks, while still staying compact — it grows to the
              // content instead of a fixed-height textarea.
              <div
                aria-label="Default Content"
                className="w-full whitespace-pre-wrap break-words rounded border border-border-medium bg-bg-disabled px-2 py-1 text-sm text-text-tertiary"
              >
                {field.content}
              </div>
            ) : (
              <p className="text-sm italic text-text-tertiary">No default content set</p>
            )
          ) : (
            <textarea
              aria-label="Default Content"
              value={field.content ?? ""}
              onChange={(e) => onUpdate({ content: e.target.value })}
              rows={3}
              placeholder="Starter text pre-filled into new fields"
              className="w-full resize-none rounded border border-border-medium bg-bg-surface px-2 py-1 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-limestone-400"
            />
          )}
        </div>
      )}
    </div>
  );
};
